import React, { useState, useMemo } from "react";
import { DOC_CENTER } from "@/constants/testIds";
import { Search, Ship, Building2, Box, ClipboardCheck, FileText, BadgeCheck, CalendarDays, Command, GitCommit } from "lucide-react";

const FILTERS = [
  { slug: "shipment", label: "Shipment", icon: Ship },
  { slug: "supplier", label: "Supplier", icon: Building2 },
  { slug: "container", label: "Container", icon: Box },
  { slug: "purchase-order", label: "Purchase Order", icon: ClipboardCheck },
  { slug: "document-type", label: "Document Type", icon: FileText },
  { slug: "status", label: "Status", icon: BadgeCheck },
  { slug: "date", label: "Date", icon: CalendarDays },
];

const RESULTS = [
  { slug: "po-2481", title: "Purchase Order · PO-2481-A", meta: "Shipment SH-2481 · ABC Foods · v3.2", tag: "Approved" },
  { slug: "ci-44821", title: "Commercial Invoice · CI-44821", meta: "Shipment SH-2481 · ABC Foods · v2.1", tag: "Approved" },
  { slug: "bl-2481", title: "Bill of Lading · BL-MSCU-2481", meta: "Container MSCU1234567 · MSC Line · v1.0", tag: "Approved" },
  { slug: "ins-7740", title: "Inspection Report · INS-7740", meta: "Inspection™ · 12 June · v1.0", tag: "Approved" },
  { slug: "coo-9912", title: "Certificate of Origin · COO-IN-9912", meta: "ABC Foods · India · v1.1", tag: "Approved" },
];

export default function SmartSearch() {
  const [q, setQ] = useState("");
  const [active, setActive] = useState(["shipment"]);

  const toggle = (slug) =>
    setActive((arr) => (arr.includes(slug) ? arr.filter((x) => x !== slug) : [...arr, slug]));

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return RESULTS;
    return RESULTS.filter((r) => r.title.toLowerCase().includes(term) || r.meta.toLowerCase().includes(term));
  }, [q]);

  return (
    <section data-testid={DOC_CENTER.search.section} className="relative bg-white py-28 md:py-36">
      <div className="dc-container">
        <div className="mx-auto max-w-2xl text-center">
          <span className="dc-eyebrow">Smart search</span>
          <h2 className="mt-4 font-display text-4xl font-black leading-[1.05] tracking-tighter text-ink-950 sm:text-5xl md:text-6xl">
            Find any document<br />in milliseconds.
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-[15px] leading-relaxed text-ink-600">
            Search by shipment, supplier, container, PO, document type, status or date. Results appear as you type.
          </p>
        </div>

        {/* Search box */}
        <div className="mx-auto mt-12 max-w-3xl">
          <div className="dc-card relative overflow-hidden p-1.5 shadow-[0_24px_60px_-24px_rgba(15,23,42,0.18)]">
            <div className="flex items-center gap-3 rounded-xl bg-white px-4 py-3">
              <Search className="h-5 w-5 text-ink-500" strokeWidth={1.75} />
              <input
                data-testid={DOC_CENTER.search.input}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search documents…"
                className="flex-1 bg-transparent text-[16px] text-ink-950 placeholder:text-ink-400 focus:outline-none"
              />
              <span className="hidden items-center gap-1 rounded-md border border-[color:var(--dc-border)] px-2 py-0.5 font-mono text-[11px] text-ink-500 sm:inline-flex">
                <Command className="h-3 w-3" strokeWidth={1.75} /> K
              </span>
            </div>

            <div className="flex flex-wrap gap-1.5 border-t border-[color:var(--dc-border)] px-3 py-3">
              {FILTERS.map((f) => {
                const Icon = f.icon;
                const isActive = active.includes(f.slug);
                return (
                  <button
                    key={f.slug}
                    data-testid={DOC_CENTER.search.chip(f.slug)}
                    onClick={() => toggle(f.slug)}
                    className={`group inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[12px] font-medium transition-all duration-200 ${
                      isActive
                        ? "border-ink-950 bg-ink-950 text-white"
                        : "border-[color:var(--dc-border)] bg-white text-ink-700 hover:border-ink-300"
                    }`}
                  >
                    <Icon className="h-3.5 w-3.5" strokeWidth={1.75} />
                    {f.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Results */}
          <div className="mt-4 overflow-hidden rounded-2xl border border-[color:var(--dc-border)] bg-white">
            <div className="flex items-center justify-between border-b border-[color:var(--dc-border)] px-4 py-2.5">
              <span className="font-mono text-[10.5px] uppercase tracking-wider text-ink-500">
                {filtered.length} results · sorted by relevance
              </span>
              <span className="font-mono text-[10.5px] text-ink-400">~ 38 ms</span>
            </div>

            <ul>
              {filtered.length === 0 && (
                <li className="px-5 py-10 text-center text-sm text-ink-500">No documents match “{q}”.</li>
              )}
              {filtered.map((r, i) => (
                <li
                  key={r.slug}
                  data-testid={DOC_CENTER.search.result(r.slug)}
                  className="flex items-center justify-between gap-3 border-b border-[color:var(--dc-border)] px-5 py-3.5 transition-colors hover:bg-[color:var(--dc-bg)] last:border-b-0 dc-rise"
                  style={{ animationDelay: `${i * 40}ms` }}
                >
                  <div className="flex items-start gap-3">
                    <div className="grid h-8 w-8 place-items-center rounded-md border border-[color:var(--dc-border)] bg-white text-ink-700">
                      <FileText className="h-4 w-4" strokeWidth={1.5} />
                    </div>
                    <div>
                      <div className="text-[14px] font-semibold text-ink-950">{r.title}</div>
                      <div className="mt-0.5 font-mono text-[11px] uppercase tracking-wider text-ink-500">{r.meta}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center gap-1 rounded-md bg-[color:var(--dc-approved-bg)] px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-[color:var(--dc-approved-text)]">
                      <GitCommit className="h-3 w-3" strokeWidth={2} /> {r.tag}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
